<template>
  <el-form-item label="Targeting">
    <el-collapse v-model="activePanels">
      <el-collapse-item title="Spots & Domains" name="traffic_sources">
        <el-form-item label="Spots filter">
          <RonTypeSelector
            v-model="flatDeal.ron_type"
            :disabled="disabled"
            @update:modelValue="updateRonType"
          />
        </el-form-item>
        <el-form-item
          v-if="showSpotsSelector"
          prop="spot_ids"
          :error="errors.spot_ids"
          label="Spots"
        >
          <SpotsSelector
            v-model="flatDeal.spot_ids"
            :stream="flatDeal.stream"
            :disabled="disabled"
            :type-id="flatDeal.type_id"
            @update:modelValue="updateSpots"
          />
        </el-form-item>
        <el-form-item v-if="suitableSpots.length" label="Suitable spots">
          <el-checkbox-group v-model="flatDeal.spot_ids" size="small">
            <el-checkbox-button
              v-for="spotId in suitableSpots"
              :key="spotId"
              :value="spotId"
            >
              {{ `Spot: ${spotId}` }}
            </el-checkbox-button>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item :error="errors.whitelist_domains" label="Domain filter">
          <WhiteBlackLists
            v-model="flatDeal.use_whitelist_domains"
            :disabled="true"
            title="Domains"
            type="domains"
            hint="One domain per line, e.g.: example.com (without https:// and www)"
            :black-list="flatDeal.blacklist_domains"
            :white-list="flatDeal.whitelist_domains"
            @update="update"
          />
        </el-form-item>
      </el-collapse-item>
      <el-collapse-item title="Geo" name="geo">
        <CountrySelector
          v-model="flatDeal.country_codes"
          @update:modelValue="updateCountries"
        />
      </el-collapse-item>
      <el-collapse-item title="Device settings" name="device">
        <DeviceSelector
          v-model="flatDeal.device_ids"
          @update:modelValue="updateDevices"
        />
      </el-collapse-item>
    </el-collapse>
  </el-form-item>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import CountrySelector from '@/apps/traforama/components/CountrySelector.vue'
import DeviceSelector from '@/apps/traforama/components/DeviceSelector.vue'
import WhiteBlackLists from '@/apps/traforama/components/WhiteBlackLists.vue'
import RonTypeSelector from '@/apps/traforama/views/campaigns/components/RonTypeSelector.vue'
import SpotsSelector from '@/apps/traforama/components/SpotsSelector.vue'
import { IMarketplaceStatPayload } from '@/apps/traforama/interfaces/marketplaceStat'
import useResize from '@/hooks/useResize'
import { useMarketplaceStore } from '@/apps/traforama/store/marketplace'

import { RonType, FlatDealValidationErrorsKey } from '@/api/types'
import { useUserStore } from '@/store/modules/user'
import useValidation from '@/hooks/useValidation'

export default defineComponent({
  name: 'FlatDealTargeting',
  components: {
    CountrySelector,
    DeviceSelector,
    RonTypeSelector,
    SpotsSelector,
    WhiteBlackLists
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    }
  },
  setup(props, context) {
    const marketplaceStore = useMarketplaceStore()
    const userStore = useUserStore()

    const { isStaff } = userStore
    const flatDeal = ref()
    const suitableSpots = ref<number[]>([])
    const { isDesktop } = useResize()
    const activePanels = reactive<Array<string>>([])

    const { validationErrors } = useValidation(FlatDealValidationErrorsKey)
    const errors = validationErrors()

    flatDeal.value = { ...props.modelValue }

    const requestFilters = {
      type_id: flatDeal.value.type_id,
      stream: flatDeal.value.stream,
      size_id: flatDeal.value.size_id,
      spot_id: flatDeal.value.spot_id,
      deal_platform_id: flatDeal.value.deal_platform_id
    } as any
    marketplaceStore
      .SuitableSpots({ filter: requestFilters } as IMarketplaceStatPayload)
      .then((res) => {
        suitableSpots.value = res || []
      })

    const showSpotsSelector = computed(() => {
      return flatDeal.value.ron_type !== RonType.All
    })

    const disabled = computed(() => {
      return !isStaff
    })

    if (flatDeal.value.domainsChanged) {
      activePanels.push('traffic_sources')
    }

    watch(
      (_: any) => props.modelValue,
      (val: any) => {
        Object.assign(flatDeal.value, val)
        if (val.domainsChanged && !activePanels.includes('traffic_sources')) {
          activePanels.push('traffic_sources')
        }
      },
      { deep: true }
    )

    const emitUpdate = (overrides: Record<string, any> = {}) => {
      context.emit('update', {
        spot_ids: flatDeal.value.spot_ids,
        country_codes: flatDeal.value.country_codes,
        device_ids: flatDeal.value.device_ids,
        use_whitelist_domains: flatDeal.value.use_whitelist_domains,
        whitelist_domains: flatDeal.value.whitelist_domains,
        ...overrides
      })
    }

    const update = ({
      whiteList,
      type
    }: {
      whiteList?: string[]
      type?: string
    }) => {
      if (type === 'domains') {
        flatDeal.value.use_whitelist_domains = true
        flatDeal.value.whitelist_domains = whiteList
        emitUpdate({
          use_whitelist_domains: true,
          whitelist_domains: whiteList
        })
      } else {
        emitUpdate()
      }
    }

    const updateCountries = (val: string[]) => {
      flatDeal.value.country_codes = val
      emitUpdate({ country_codes: val })
    }

    const updateDevices = (val: number[]) => {
      flatDeal.value.device_ids = val
      emitUpdate({ device_ids: val })
    }

    const updateSpots = (val: number[]) => {
      flatDeal.value.spot_ids = val
      emitUpdate({ spot_ids: val })
    }

    const updateRonType = (val: string) => {
      flatDeal.value.ron_type = val
      emitUpdate()
    }

    return {
      showSpotsSelector,
      activePanels,
      isDesktop,
      flatDeal,
      isStaff,
      errors,
      disabled,
      update,
      updateCountries,
      updateDevices,
      updateSpots,
      updateRonType,
      suitableSpots
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.domains-wrap) {
  margin: 0;
}
</style>
